import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthResponse, LoginPayload, SignupPayload, ResetPasswordPayload } from '../models/auth.models';
import { PaymentService } from './payment.service';

const ACCESS_TOKEN_KEY = 'nnx_access_token';
const REFRESH_TOKEN_KEY = 'nnx_refresh_token';
const USER_KEY = 'nnx_user';
const PENDING_PLAN_KEY = 'nnx_pending_plan_id';

@Injectable({ providedIn: 'root' })
export class AuthService {
  // Reactive signal the navbar/dashboard can read to show logged-in state
  currentUser = signal<AuthResponse | null>(this.readStoredUser());

  constructor(private http: HttpClient, private router: Router, private payments: PaymentService) {}

  private readStoredUser(): AuthResponse | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthResponse) : null;
  }

  get accessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  get refreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.accessToken;
  }

  /**
   * Call before sending the user to /login when they clicked "Pay" on a plan while logged out.
   * Once they log in or sign up, persistSession() will pick this up and go straight to
   * PhonePe checkout instead of the dashboard — login is only ever an interruption, not a destination.
   */
  setPendingPlanPurchase(planId: number): void {
    localStorage.setItem(PENDING_PLAN_KEY, String(planId));
  }

  private consumePendingPlanPurchase(): number | null {
    const raw = localStorage.getItem(PENDING_PLAN_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_PLAN_KEY);
    return Number(raw);
  }

  /**
   * Loads the Google Identity Services button into the given DOM element.
   * Call from a component's ngAfterViewInit once the div is in the DOM.
   */
  renderGoogleButton(container: HTMLElement): void {
    if (!window.google) {
      // Script tag in index.html loads asynchronously; retry shortly if not ready yet
      setTimeout(() => this.renderGoogleButton(container), 300);
      return;
    }

    window.google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response) => this.handleGoogleCredential(response.credential),
      ux_mode: 'popup',
    });

    window.google.accounts.id.renderButton(container, {
      theme: 'filled_blue',
      size: 'large',
      shape: 'pill',
      width: 320,
      text: 'continue_with',
    });
  }

  private handleGoogleCredential(idToken: string): void {
    this.http
      .post<AuthResponse>(`${environment.apiUrl}/auth/google`, { idToken })
      .subscribe({
        next: (res) => this.persistSession(res),
        error: (err) => console.error('Google login failed', err),
      });
  }

  requestSignupOtp(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/signup/request-otp`, { email });
  }

  signup(payload: SignupPayload): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${environment.apiUrl}/auth/signup`, payload)
      .pipe(tap((res) => this.persistSession(res)));
  }

  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${environment.apiUrl}/auth/login`, payload)
      .pipe(tap((res) => this.persistSession(res)));
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/password/forgot`, { email });
  }

  resetPassword(payload: ResetPasswordPayload): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/password/reset`, payload);
  }

  private persistSession(res: AuthResponse): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, res.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, res.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(res));
    this.currentUser.set(res);

    const pendingPlanId = this.consumePendingPlanPurchase();
    if (pendingPlanId) {
      // User was mid-checkout when we asked them to log in — skip the dashboard and go
      // straight to PhonePe checkout for the plan they already picked.
this.payments.initiatePhonePePayment(pendingPlanId).subscribe({
        next: (r) => this.payments.redirectToCheckout(r.redirectUrl),
        error: () => this.router.navigate(['/dashboard/renewal'], { queryParams: { paymentError: '1' } }),
      });
    } else {
      this.router.navigate(['/dashboard/overview']);
    }
  }

  logout(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }
}
